// Guard Module Types

// ============================================
// Directory Types
// ============================================

export type ResidentRelationship = 'TITULAR' | 'FAMILIAR' | 'INQUILINO' | 'OTRO';

export interface DirectoryResident {
  id?: string;
  memberId: string;
  name: string;
  phone?: string | null;
  photoUrl?: string | null;
  unitId: string;
  unitName: string;
  /** Alias for unitName — used by guard directory screen */
  unit?: string;
  tower?: string | null;
  floor?: string | null;
  relationship: ResidentRelationship;
  /** Solo para administradores: el backend lo omite para guardias, igual que phone. */
  isDelinquent?: boolean;
  /** Vehicle plates associated with the resident */
  vehicles?: string[];
}

export interface DirectoryParams {
  search?: string;
  page?: number;
  limit?: number;
}

// ============================================
// Delinquent Units Types
// ============================================

export interface DelinquentUnit {
  id?: string;
  unitId: string;
  unitName: string;
  /** Alias for unitName — used by guard delinquent screen */
  unit?: string;
  tower?: string | null;
  residentName?: string | null;
  monthsOverdue: number;
  totalDebt: number;
}

// ============================================
// Guard Dashboard Types
// ============================================

export interface GuardShiftInfo {
  id: string;
  startTime: string;
  guardName: string;
}

// Misma forma que TodayStatsDto del backend (guard-shift.dto.ts); antes se leían
// entries/exits/visitors/incidents, campos que el backend nunca envió.
export interface GuardTodayStats {
  totalAccesses: number;
  residentAccesses: number;
  guestAccesses: number;
  deniedAccesses: number;
}

export interface GuardActivityItem {
  type: string;
  description: string;
  timestamp: string;
}

export type AlertPriority = 'low' | 'medium' | 'high';

export interface GuardAlert {
  id: string;
  type: string;
  message: string;
  priority: AlertPriority;
}

export interface GuardDashboard {
  currentShift?: GuardShiftInfo;
  todayStats: GuardTodayStats;
  recentActivity: GuardActivityItem[];
  activeAlerts: GuardAlert[];
}

// ============================================
// Shift Types
// ============================================

export type ShiftStatus = 'ACTIVE' | 'COMPLETED' | 'ABANDONED';

export interface Shift {
  id: string;
  guardMasterUserId: string;
  guardName: string;
  status: ShiftStatus;
  startTime: string;
  endTime: string | null;
  duration?: string | null;
  accessCount?: number;
  incidentCount?: number;
  entryPoint?: string | null;
  notes: string | null;
  startLatitude?: number | null;
  startLongitude?: number | null;
  endLatitude?: number | null;
  endLongitude?: number | null;
  createdAt: string;
}

export interface CurrentShiftResponse {
  hasActiveShift: boolean;
  shift?: Shift;
}

export interface ShiftWithDetails extends Shift {
  logbookEntries?: import('../logbook').LogbookEntry[];
  patrolRounds?: import('../logbook').PatrolRound[];
}

export interface ShiftParams {
  page?: number;
  limit?: number;
  status?: ShiftStatus;
  guardId?: string;
  dateFrom?: string;
  dateTo?: string;
}

// ============================================
// Guard Access Log Types (Different from access-control AccessLog)
// ============================================

export type GuardAccessType = 'ENTRY' | 'EXIT';
export type GuardAccessMethod = 'MANUAL' | 'QR' | 'RFID' | 'FACIAL' | 'PLATE';
export type GuardPersonType = 'RESIDENT' | 'VISITOR' | 'DELIVERY' | 'SERVICE' | 'EMPLOYEE';

export interface GuardAccessLog {
  id: string;
  type: GuardAccessType;
  method: GuardAccessMethod;
  personType: GuardPersonType;
  personName: string;
  personDocument?: string;
  vehiclePlate?: string;
  unitId?: string;
  unitNumber?: string;
  registeredByGuardId: string;
  registeredByGuardName: string;
  notes?: string;
  photoUrl?: string;
  timestamp: string;
}

export interface CreateGuardAccessLogDTO {
  type: GuardAccessType;
  method: GuardAccessMethod;
  personType: GuardPersonType;
  personName: string;
  personDocument?: string;
  vehiclePlate?: string;
  unitId?: string;
  notes?: string;
  photoUrl?: string;
  latitude?: number;
  longitude?: number;
}

export interface GuardAccessLogParams {
  page?: number;
  limit?: number;
  type?: GuardAccessType;
  method?: GuardAccessMethod;
  personType?: GuardPersonType;
  unitId?: string;
  dateFrom?: string;
  dateTo?: string;
  search?: string;
}

// ============================================
// Restriction Types
// ============================================

export type RestrictionType = 'PERSON' | 'VEHICLE' | 'GENERAL' | 'VISITOR';

export interface Restriction {
  id: string;
  type: RestrictionType;
  name?: string;
  documentId?: string;
  /** Alias for documentId used by guard restrictions screen */
  documentNumber?: string;
  licensePlate?: string;
  /** Alias for licensePlate used by guard restrictions screen */
  vehiclePlate?: string;
  reason: string;
  /** Placa o documento, según el tipo (RestrictionResponseDto.identifier). */
  identifier?: string;
  /** Human-readable description of the restriction */
  description?: string;
  createdBy: string;
  createdAt: string;
  expiresAt?: string;
  active: boolean;
}

export interface CreateRestrictionRequest {
  type: RestrictionType;
  name?: string;
  documentId?: string;
  licensePlate?: string;
  reason: string;
  expiresAt?: string;
}

/** Restricciones activas de la unidad del residente actual (GET /restrictions/me). */
export interface MyRestrictions {
  hasRestrictions: boolean;
  restrictedActions: string[];
  /** Adeudo total de la unidad en MXN (0 si no aplica). */
  debtAmount: number;
  /** Si la restricción activa proviene del bloqueo por cobranza/adeudo. */
  isCobranza: boolean;
  /**
   * Listado Negro: bloqueo TOTAL. El residente solo conserva el módulo de
   * pagos; cualquier otro endpoint responde 403 UNIT_BLACKLISTED.
   */
  isBlacklisted: boolean;
}

/** Código que devuelve el backend en un 403 por Listado Negro. */
export const BLACKLIST_ERROR_CODE = 'UNIT_BLACKLISTED';

// ============================================
// Emergency Types
// ============================================

export interface EmergencyContact {
  id: string;
  name: string;
  phone: string;
  role: string;
  priority: number;
  type?: 'POLICE' | 'FIRE' | 'AMBULANCE' | 'SECURITY' | 'ADMIN' | 'OTHER';
  description?: string;
}

export interface PanicAlertRequest {
  location?: string;
  details?: string;
}

// Ningún estado implica recepción: REGISTERED = en la bandeja de un admin,
// SENT = un canal externo (push) la aceptó, READ = un admin la marcó como leída.
export type PanicAlertStatus = 'REGISTERED' | 'SENT' | 'READ';

export interface PanicAlertResponse {
  alertId: string;
  status: PanicAlertStatus;
  message: string;
  timestamp: string;
  registeredAdmins: number;
  notifiedAdmins: number;
  readAdmins: number;
}

// ============================================
// GPS Routes (SDD §6) - forma de RouteResponseDto del backend
// ============================================

export type GuardRouteStatus = 'IN_PROGRESS' | 'COMPLETED';

export interface GuardRouteStop {
  latitude: number;
  longitude: number;
  startedAt: string;
  endedAt: string;
  durationSeconds: number;
}

export interface GuardRouteGap {
  startedAt: string;
  endedAt: string;
  durationSeconds: number;
}

export interface GuardRoute {
  id: string;
  shiftId: string;
  guardMasterUserId: string;
  guardName: string | null;
  status: GuardRouteStatus;
  startedAt: string;
  endedAt: string | null;
  distanceMeters: number;
  durationSeconds: number;
  pointCount: number;
  stops: GuardRouteStop[];
  gaps: GuardRouteGap[];
}

export interface GuardRoutePoint {
  latitude: number;
  longitude: number;
  accuracy?: number | null;
  altitude?: number | null;
  speed?: number | null;
  recordedAt: string;
}

export interface RouteHistoryParams {
  page?: number;
  limit?: number;
  /** Solo lo respeta el backend para el admin; a un guardia siempre le devuelve los suyos. */
  guardId?: string;
  status?: GuardRouteStatus;
  dateFrom?: string;
  dateTo?: string;
}

export interface GuardRouteDetail extends GuardRoute {
  points: GuardRoutePoint[];
}

// Contexto de captura al iniciar turno (SDD §5.2).
export interface ShiftStartContext {
  clientEventId?: string;
  accuracy?: number;
  capturedAt?: string;
  gpsStatus?: 'OK' | 'DENIED' | 'UNAVAILABLE';
  networkStatus?: 'ONLINE' | 'OFFLINE';
  locationExceptionReason?: string;
}
